import moment from 'moment';
import { useState } from 'react';
import { Skeleton, IconButton, Switch, Tooltip } from '@mui/material';

import PenIcon from 'app/assets/icons/pen.svg?react';
import CheckmarkIcon from 'app/assets/icons/checkmark-3.svg?react';
import CloseIcon from 'app/assets/icons/close-2.svg?react';
import InfoIcon from 'app/assets/icons/info-filled.svg?react';

import { HealtheraInput } from 'app/shared-components/forms';

export default function Field({
  title,
  icon: Icon,
  value,
  capitalize,
  type,
  loading,
  onEdit,
  isSwitch,
  checked,
  onChange,
  disabled,
  tooltip,
}) {
  const [isEditMode, setIsEditMode] = useState(false);
  const [newValue, setNewValue] = useState(value);

  const valueClasses = ['font-600', 'leading-[26px]'];
  if (capitalize) valueClasses.push('capitalize');
  if (capitalize) value = value?.toLowerCase();

  if (type === 'unix' && typeof value === 'number') {
    value = moment.unix(value).format('MMMM D, YYYY');
  }

  if (type === 'address' && value && typeof value === 'object') {
    value = [value.address_line_1, value.address_line_2, value.city, value.county, value.postcode]
      .map((piece) => piece?.trim())
      .filter(Boolean)
      .join(', ');
  }

  function enterEditMode() {
    setIsEditMode(true);
    setNewValue(value);
  }

  async function submitEdit() {
    await onEdit(newValue);
    setIsEditMode(false);
  }

  function resetValue() {
    setIsEditMode(false);
    setNewValue(value);
  }

  if (isSwitch) {
    return (
      <div className="flex gap-8">
        <div className="flex-1">
          <div className="flex items-center gap-8">
            {Icon && <Icon className="w-24 h-24" />}
            <div className="text-grey-40 font-600 text-12">{title}</div>
            {tooltip && (
              <Tooltip title={tooltip}>
                <InfoIcon className="w-18 h-18 ml-5 text-grey-40" />
              </Tooltip>
            )}
          </div>
          {loading ? (
            <Skeleton width={120} height={26} />
          ) : (
            <div className="flex items-center gap-4 mt-4">
              <Switch checked={checked} onChange={onChange} disabled={disabled} />
              <span className="font-600 leading-[26px]">{value}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-8 items-end">
      <div className="shrink-0 h-26">
        <Icon className="h-full" />
      </div>
      <div>
        <div className="text-grey-40 font-600 text-12">{title}</div>
        {tooltip && (
          <Tooltip title={tooltip}>
            <InfoIcon className="w-18 h-18 ml-5 text-grey-40" />
          </Tooltip>
        )}
        <div>
          {loading && <Skeleton className="w-200" animation="wave" variant="text" />}
          {!loading && !isEditMode && <div className={valueClasses.join(' ')}>{value || '-'}</div>}
          {!loading && isEditMode && (
            <HealtheraInput value={newValue} onChange={setNewValue} padding="xs" />
          )}
        </div>
      </div>
      {Boolean(onEdit) && !isEditMode && !loading && (
        <IconButton
          className="p-4 rounded-none self-end"
          onClick={enterEditMode}
          title={`Edit ${title}`}
        >
          <PenIcon className="text-black w-18 h-18" />
        </IconButton>
      )}
      {Boolean(onEdit) && isEditMode && !loading && (
        <>
          <IconButton className="p-4 rounded-none self-end" onClick={submitEdit} title="Save">
            <CheckmarkIcon className="text-green w-18 h-18" />
          </IconButton>
          <IconButton className="p-4 rounded-none self-end" onClick={resetValue} title="Cancel">
            <CloseIcon className="text-red w-18 h-18" />
          </IconButton>
        </>
      )}
    </div>
  );
}
